import { CreateMLCEngine, CreateWebWorkerMLCEngine, hasModelInCache, prebuiltAppConfig } from '@mlc-ai/web-llm';

export const LOCAL_MODELS = {
  fast: { base: 'Qwen2.5-0.5B-Instruct', label: 'Lindey Fast', size: '~400 MB', desc: 'Quickest replies, lightest download', vram: { f16: 950, f32: 1150 } },
  thinking: { base: 'Llama-3.2-1B-Instruct', label: 'Lindey Thinking', size: '~800 MB', desc: 'Balanced speed and smarts', vram: { f16: 1130, f32: 1450 } },
  pro: { base: 'Llama-3.2-3B-Instruct', label: 'Lindey Pro', size: '~1.8 GB', desc: 'Smartest on-device model', vram: { f16: 2260, f32: 2950 } },
};

const DOWNGRADE = { pro: 'thinking', thinking: 'fast', fast: null };

let engine = null;
let loadedTier = null;
let loadingPromise = null;
let gpuInfo = null;
let lastLocalError = null;

export function getLastLocalError() { return lastLocalError; }

export function isWebGPUSupported() {
  return typeof navigator !== 'undefined' && !!navigator.gpu;
}

export async function probeWebGPU(force = false) {
  if (gpuInfo && !force) return gpuInfo;
  try {
    if (!navigator.gpu) return { supported: false, reason: 'This browser has no WebGPU API (try Chrome or Edge).' };
    let adapter = null;
    try { adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' }); } catch { /* */ }
    if (!adapter) { try { adapter = await navigator.gpu.requestAdapter(); } catch { /* */ } }
    if (!adapter) { try { adapter = await navigator.gpu.requestAdapter({ forceFallbackAdapter: true }); } catch { /* */ } }
    if (!adapter) {
      // do NOT cache negatives — a later probe may succeed
      return { supported: false, reason: 'No GPU adapter found. Enable hardware acceleration (chrome://settings/system) and restart the browser.' };
    }
    gpuInfo = {
      supported: true,
      f16: !!(adapter.features && adapter.features.has('shader-f16')),
      maxBufferMB: Math.floor((adapter.limits?.maxBufferSize || 0) / (1024 * 1024)),
    };
    return gpuInfo;
  } catch (e) {
    return { supported: false, reason: `WebGPU probe failed: ${e?.message || e}` };
  }
}

export async function getCachedModels() {
  const out = {};
  for (const [tier, m] of Object.entries(LOCAL_MODELS)) {
    out[tier] = false;
    for (const suf of ['q4f16_1', 'q4f32_1']) {
      try {
        if (await hasModelInCache(`${m.base}-${suf}-MLC`)) { out[tier] = true; break; }
      } catch { /* */ }
    }
  }
  return out;
}

// Pre-download a model (warms + caches it so later use is instant)
export function downloadModel(tier, onProgress) {
  return getLocalEngine(tier, onProgress);
}

export function hasExplicitTier() {
  try { return !!localStorage.getItem('lindey_local_tier'); } catch { return false; }
}

// Best tier that provably fits this device
export function recommendTier(gpu) {
  const budget = deviceBudgetMB(gpu);
  if (gpu.f16 && budget >= 3000) return 'pro';
  if (budget >= 1800) return 'thinking';
  return 'fast';
}

// Does this tier fit the device's GPU budget?
export function tierFits(tier, gpu) {
  if (!gpu?.supported) return false;
  const m = LOCAL_MODELS[tier];
  const need = gpu.f16 ? m.vram.f16 : m.vram.f32;
  return need <= deviceBudgetMB(gpu);
}

// Off-main-thread engine so the UI never freezes during generation
async function createEngine(modelId, config) {
  try {
    const worker = new Worker(new URL('../workers/webllmWorker.js', import.meta.url), { type: 'module' });
    return await CreateWebWorkerMLCEngine(worker, modelId, config);
  } catch (err) {
    console.warn('Worker engine unavailable, falling back to main thread', err);
    return CreateMLCEngine(modelId, config);
  }
}

function getLastGood() {
  try { return JSON.parse(localStorage.getItem('lindey_last_model') || 'null'); } catch { return null; }
}
function setLastGood(v) {
  try { localStorage.setItem('lindey_last_model', JSON.stringify(v)); } catch { /* */ }
}

// 5s preflight: can this network reach the model CDN directly?
async function directCdnHealthy() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    const r = await fetch('https://huggingface.co/mlc-ai/Qwen2.5-0.5B-Instruct-q4f32_1-MLC/resolve/main/mlc-chat-config.json', { signal: ctrl.signal, cache: 'no-store' });
    clearTimeout(t);
    return r.ok;
  } catch { return false; }
}

// Lindey download relay: streams model files through our backend when CDNs are blocked/rate-limited
function relayAppConfig(modelId) {
  const rec = prebuiltAppConfig.model_list.find(m => m.model_id === modelId);
  if (!rec) return null;
  const base = process.env.REACT_APP_BACKEND_URL;
  return {
    ...prebuiltAppConfig,
    model_list: [{
      ...rec,
      model: rec.model.replace('https://huggingface.co/', `${base}/api/model-cdn/hf/`),
      model_lib: rec.model_lib.replace('https://raw.githubusercontent.com/', `${base}/api/model-cdn/gh/`),
    }],
  };
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export function getSavedTier() {
  try {
    const saved = localStorage.getItem('lindey_local_tier');
    if (saved) return saved;
    // Scheduler: phones start on Fast to save battery; everything else defaults to Pro
    const mobile = /Android|iPhone|iPad|Mobi/i.test(navigator.userAgent);
    return mobile ? 'fast' : 'pro';
  } catch { return 'pro'; }
}

export function saveTier(tier) {
  try { localStorage.setItem('lindey_local_tier', tier); } catch { /* */ }
}

function deviceBudgetMB(gpu) {
  let ram = 8;
  try { ram = navigator.deviceMemory || 8; } catch { /* */ }
  const buf = gpu.maxBufferMB || 2048;
  return Math.min((ram * 1024) / 2, Math.max(buf, 1200));
}

function tierChain(tier) {
  const chain = [];
  let cur = tier;
  while (cur) { chain.push(cur); cur = DOWNGRADE[cur]; }
  return chain;
}

export async function getLocalEngine(tier, onProgress) {
  if (engine && loadedTier === tier) return { engine, tierUsed: loadedTier };
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    if (engine) {
      try { await engine.unload(); } catch { /* */ }
      engine = null; loadedTier = null;
    }
    const gpu = await probeWebGPU();
    if (!gpu.supported) {
      lastLocalError = gpu.reason || 'WebGPU unavailable';
      throw new Error('webgpu-unavailable');
    }
    const budget = deviceBudgetMB(gpu);
    // Build candidates: f16 builds only when supported, f32 works everywhere
    let candidates = [];
    for (const t of tierChain(tier)) {
      const m = LOCAL_MODELS[t];
      const variants = gpu.f16 ? [`${m.base}-q4f16_1-MLC`, `${m.base}-q4f32_1-MLC`] : [`${m.base}-q4f32_1-MLC`];
      for (const id of variants) candidates.push({ tier: t, id, need: id.includes('f16') ? m.vram.f16 : m.vram.f32 });
    }
    // Scheduler: only attempt models that fit this GPU (no doomed multi-minute downloads)
    const fitting = candidates.filter(c => c.need <= budget);
    if (fitting.length) candidates = fitting;
    else candidates = [candidates[candidates.length - 1]]; // nothing fits — try the smallest only
    // Already-cached models first (instant), then the last model known to work on this device
    let ordered = candidates;
    try {
      const cached = await Promise.all(candidates.map(c => hasModelInCache(c.id).catch(() => false)));
      ordered = [
        ...candidates.filter((_, i) => cached[i]),
        ...candidates.filter((_, i) => !cached[i]),
      ];
    } catch { /* keep default order */ }
    const lastGood = getLastGood();
    if (lastGood) {
      const idx = ordered.findIndex(c => c.id === lastGood.id);
      if (idx > 0) ordered.unshift(ordered.splice(idx, 1)[0]);
    }
    // Skip direct CDN entirely when the network can't reach it (blocked wifi etc.)
    const direct = await directCdnHealthy();
    let lastErr = null;
    for (const c of ordered) {
      const attempts = direct ? [{ relay: false }, { relay: true }] : [{ relay: true }, { relay: true }];
      for (let i = 0; i < attempts.length; i++) {
        try {
          if (i > 0) await sleep(1500);
          const useRelay = attempts[i].relay;
          const relayCfg = useRelay ? relayAppConfig(c.id) : null;
          if (useRelay && !relayCfg) continue;
          onProgress?.({ progress: 0, text: useRelay ? `Preparing ${LOCAL_MODELS[c.tier].label} via Lindey relay…` : `Preparing ${LOCAL_MODELS[c.tier].label}…`, tier: c.tier });
          const e = await createEngine(c.id, {
            initProgressCallback: (r) => onProgress?.({ progress: r.progress || 0, text: r.text || '', tier: c.tier }),
            ...(relayCfg ? { appConfig: relayCfg } : {}),
          });
          // Runtime smoke test (60s cap): prove the model generates on this GPU before declaring ready.
          // A timeout means slow shader compile, not failure — accept and let it finish warming.
          try {
            onProgress?.({ progress: 1, text: 'Verifying model on your device…', tier: c.tier });
            const smoke = e.chat.completions.create({ messages: [{ role: 'user', content: 'hi' }], max_tokens: 1, temperature: 0 })
              .then(() => 'ok').catch(err => ({ err }));
            const outcome = await Promise.race([smoke, sleep(60000).then(() => 'timeout')]);
            if (outcome && outcome.err) throw outcome.err;
          } catch (smokeErr) {
            try { await e.unload(); } catch { /* */ }
            throw smokeErr;
          }
          engine = e; loadedTier = c.tier;
          setLastGood({ id: c.id, tier: c.tier });
          return { engine: e, tierUsed: c.tier };
        } catch (err) {
          lastErr = err;
          lastLocalError = err?.message || String(err);
          console.warn(`Scheduler: ${c.id} failed (attempt ${i + 1}${attempts[i].relay ? ', relay' : ''})`, err);
        }
      }
    }
    throw lastErr || new Error('No on-device model could be loaded');
  })();
  try { return await loadingPromise; } finally { loadingPromise = null; }
}

export async function streamLocal(activeEngine, messages, onUpdate) {
  const chunks = await activeEngine.chat.completions.create({
    messages, stream: true, temperature: 0.7, max_tokens: 1024,
  });
  let text = '';
  for await (const chunk of chunks) {
    const delta = chunk.choices?.[0]?.delta?.content || '';
    if (delta) { text += delta; onUpdate(text); }
  }
  return text;
}

export function interruptLocal() {
  try { engine?.interruptGenerate(); } catch { /* */ }
}

// ===== Lindey Heavy: CPU scheduler orchestrating up to 60 model shards =====

const HEAVY_STAGES = [
  { key: 'plan', label: 'Planning cluster', prompt: 'You are the planning cluster. Break the task into its key parts and outline the ideal answer structure.' },
  { key: 'alpha', label: 'Solver cluster Alpha', prompt: 'You are an analytical solver cluster. Work the task rigorously, step by step, building on the plan.' },
  { key: 'beta', label: 'Solver cluster Beta', prompt: 'You are a creative solver cluster. Attack the task from a different angle and add anything the other clusters may have missed.' },
  { key: 'critic', label: 'Critic cluster', prompt: 'You are the critic cluster. Find flaws, gaps or errors in the previous cluster notes and state the corrections.' },
];

async function clusterPass(activeEngine, sysPrompt, history, userContent, maxTokens) {
  const res = await activeEngine.chat.completions.create({
    messages: [
      { role: 'system', content: `${sysPrompt} You are one reasoning cluster inside Lindey Heavy, a 60-shard on-device orchestration. Be dense and concise — under 150 words.` },
      ...history,
      { role: 'user', content: userContent },
    ],
    temperature: 0.7, max_tokens: maxTokens,
  });
  return res.choices?.[0]?.message?.content || '';
}

export async function runHeavy(activeEngine, history, content, { onPhase, onStream, isCancelled } = {}) {
  const words = content.trim().split(/\s+/).length;
  const stageCount = words > 80 ? 4 : words > 25 ? 3 : 2; // reasoning clusters before synthesis
  const clusters = stageCount + 1; // + synthesis core
  const shards = clusters * 12; // up to 60
  const shortHistory = history.slice(-4);
  const stages = HEAVY_STAGES.slice(0, stageCount);
  const notes = [];

  for (let i = 0; i < stages.length; i++) {
    if (isCancelled?.()) throw new Error('cancelled');
    onPhase?.({ stage: i, total: clusters, label: stages[i].label, shards });
    const userContent = notes.length
      ? `Task: ${content}\n\nNotes from previous clusters:\n${notes.join('\n---\n')}`
      : `Task: ${content}`;
    const out = await clusterPass(activeEngine, stages[i].prompt, shortHistory, userContent, 220);
    notes.push(`[${stages[i].label}] ${out}`);
  }

  if (isCancelled?.()) throw new Error('cancelled');
  onPhase?.({ stage: stageCount, total: clusters, label: 'Synthesis core', shards });
  const chunks = await activeEngine.chat.completions.create({
    messages: [
      { role: 'system', content: 'You are the synthesis core of Lindey Heavy, merging the work of many reasoning clusters into one definitive answer. Produce the single best, polished final answer in markdown. Never mention clusters, shards or internal notes.' },
      ...shortHistory,
      { role: 'user', content: `Task: ${content}\n\nInternal cluster notes:\n${notes.join('\n---\n')}\n\nWrite the final answer now.` },
    ],
    stream: true, temperature: 0.6, max_tokens: 1200,
  });
  let text = '';
  for await (const chunk of chunks) {
    const delta = chunk.choices?.[0]?.delta?.content || '';
    if (delta) { text += delta; onStream?.(text); }
  }
  return { text, shards, clusters };
}
